import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Tooltip,
} from 'antd';
import {
  GeneralContainer,
  CustomTable,
  CustomModal,
  useCustomToast,
} from '../../../../components';
import { OnlineInventoryAPI } from '../../api/onlineInventoryAPI';
import { CheckCircleOutlined, EnvironmentOutlined, ExclamationCircleOutlined, RetweetOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import InventoryStatusExtra from './InventoryStatusExtra';
import { useAtom } from 'jotai';
import { inventoryIdAtom, inventoryStatusAtom } from '../../../../state/atoms';

interface OnlineInventoryViewProps {
  movements: Array<any>;
  isLoading: boolean;
  formikProps: any;
  refetch: () => void;
  onViewReport: (record: any) => void;
  onViewVerification: (record: any) => void;
  onViewManageState: (record: any) => void;
  onViewFiscal: (record: any) => void;
}

const OnlineInventoryProgrammedView: React.FC<OnlineInventoryViewProps> = ({
  movements,
  isLoading,
  formikProps,
  onViewReport,
  onViewVerification,
  onViewManageState,
  onViewFiscal,
  refetch,
}) => {
  const { values, setFieldValue, handleSubmit, resetForm } = formikProps;
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [filteredMovements, setFilteredMovements] = useState(movements);
  const [finalizedModal, setFinalizedModal] = useState(false);
  const [inventoryId] = useAtom(inventoryIdAtom);
  const { showToast } = useCustomToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Configurar selección de filas
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[], newSelectedRows: any[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
      setSelectedRows(newSelectedRows);
    },
  };

  useEffect(() => {
    let filteredData = movements;

    if (values.search) {
      const searchValue = values.search.toLowerCase();
      filteredData = filteredData.filter(
        (item) =>
          item.inventory_code?.toLowerCase().includes(searchValue) ||
          item.plate_code?.toLowerCase().includes(searchValue) ||
          item.item_name?.toLowerCase().includes(searchValue)
      );
    }

    setFilteredMovements(filteredData);
  }, [values, movements]);

  const handleFinalizeInventory = async () => {
    if (inventoryId === null) {
      showToast('error', 'No se ha seleccionado un inventario');
      return;
    }

    try {
      const response = await OnlineInventoryAPI.changeInventoryStatus(inventoryId, 7);

      showToast('success', response?.message || 'Proceso de inventario finalizado correctamente');
      setFinalizedModal(false);
      refetch();

    } catch (error: any) {
      showToast('error', error?.response?.data?.message || 'Error al finalizar el proceso de inventario');
    }
  };

  const paginationConfig = {
    current: currentPage,
    pageSize: pageSize,
    total: filteredMovements.length,
    onChange: (page: number, size?: number) => {
      setCurrentPage(page);
      if (size) setPageSize(size);
    },
    showSizeChanger: true,
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Código/Placa',
      dataIndex: 'plate_code',
      key: 'plate_code',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Placa alterna',
      dataIndex: 'plate_code_alternative',
      key: 'plate_code_alternative',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Responsable',
      dataIndex: 'responsible',
      key: 'responsible',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'observations',
      align: 'center',
      width: 300,
      render: (text: string) => {
        return (
          <Tooltip title={text || 'Sin registro'}>
            <div
              style={{
                maxWidth: 250,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                margin: '0 auto',
              }}
            >
              {text || 'Sin registro'}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Regional',
      dataIndex: 'regional',
      key: 'regional',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Seccional',
      dataIndex: 'sectional',
      key: 'sectional',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Dependencia',
      dataIndex: 'dependency',
      key: 'dependency',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Estado de validación',
      dataIndex: 'inventory_status',
      key: 'inventory_status',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Fecha de última verificación',
      dataIndex: 'last_verification_date',
      key: 'last_verification_date',
      sorter: true,
      align: 'center',
      render: (date: string | null) => (date ? date : 'No registrada'),
    },
    {
      title: 'Centro de costo',
      dataIndex: 'cost_center',
      key: 'cost_center',
      sorter: true,
      align: 'center',
      width: 300,
      render: (text: string) => {
        return (
          <Tooltip title={text || 'Sin registro'}>
            <div
              style={{
                maxWidth: 250,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                margin: '0 auto',
              }}
            >
              {text || 'Sin registro'}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      className: 'ml-16 mr-16',
      render: (_: any, record: any) => (
        <>
          <Tooltip title="Verificación física del bien">
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              onClick={() => {
                onViewVerification(record);
              }}
            />
          </Tooltip>
          <Tooltip title="Validar ubicación física">
            <Button
              type="link"
              icon={<EnvironmentOutlined />}
              onClick={() => {
                onViewFiscal(record);
              }}
            />
          </Tooltip>
          <Tooltip title="Novedades">
            <Button
              type="link"
              icon={<ExclamationCircleOutlined />}
              onClick={() => {
                onViewReport([record]);
              }}
            />
          </Tooltip>
          <Tooltip title="Validar cambio de estado">
            <Button
              type="link"
              icon={<RetweetOutlined />}
              onClick={() => {
                onViewManageState(record);
              }}
            />
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <GeneralContainer
      title="Mi Inventario en línea programado"
      extra={
        <InventoryStatusExtra
          date_start={movements?.[0]?.start_date || 'N/A'}
          date_end={movements?.[0]?.end_date || 'N/A'}
          status={movements?.[0]?.general_status || 'N/A'}
        />
      }
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '20px 10px 10px 10px',
        }}
      >
        <Checkbox
          onChange={(e) => {
            setSelectedRowKeys(
              e.target.checked ? movements.map((item) => item.id) : []
            )
            setSelectedRows(
              e.target.checked ? movements : []
            );
          }
          }
          checked={
            selectedRowKeys.length === movements.length && movements.length > 0
          }
        >
          Seleccionar todos
        </Checkbox>

        <div style={{ display: 'flex', gap: '10px' }}>
          <Button
            type="primary"
            onClick={
              () => onViewFiscal({
                inventory_id: selectedRows[0].inventory_id,
                selectedRowKeys
              })
            }
            disabled={selectedRowKeys.length === 0}
          >
            Validar ubicación física
          </Button>
          <Button
            type="primary"
            onClick={() => onViewReport(selectedRows)}
            disabled={selectedRowKeys.length === 0}
          >
            Novedades
          </Button>
          <Button
            type="primary"
            onClick={
              () => onViewManageState({
                inventory_id: selectedRows[0].inventory_id,
                selectedRowKeys
              })
            }
            disabled={selectedRowKeys.length === 0}
          >
            Validar cambio de estado
          </Button>
        </div>
      </div>

      <CustomTable
        columns={columns}
        dataSource={filteredMovements}
        loading={isLoading}
        rowKey="id"
        paginationConfig={paginationConfig}
        scroll={{ x: 'max-content' }}
        rowSelection={rowSelection}
        size="small"
      />

      <section style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          onClick={() => {
            setFinalizedModal(true);
          }}
          style={{ marginTop: '20px' }}
        >
          Finalizar proceso
        </Button>
      </section>

      <CustomModal
        title=""
        labelSubmit="Aceptar"
        open={finalizedModal}
        width={1000}
        onCancel={() => setFinalizedModal(false)}
        onSave={handleFinalizeInventory}
        centered
      >
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          ¿Desea finalizar el proceso de inventario y firmar el reporte de inventario en línea?
        </div>
      </CustomModal>
    </GeneralContainer>
  );
};

export default OnlineInventoryProgrammedView;
